/**
 * Public Certificate Verification Page — /verify/:certificateId
 *
 * Resolves the QR back-link printed on every co-branded Proficiency
 * Certificate. Renders without authentication:
 *   - attendee name, topic, completion date, score, instructor
 *   - "Powered by CHARSTAN · {Tenant Display Name}" co-branding banner
 *   - PDF iframe preview + direct download
 *   - Revoked banner if the certificate has been revoked
 *
 * Converts a scanned QR into a passive marketing surface that lands the
 * scanner directly on the Charstan brand without forcing a login.
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  ShieldCheck,
  Award,
  Calendar,
  User as UserIcon,
  GraduationCap,
  Globe2,
  AlertTriangle,
  Download,
  ArrowLeft,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const CertificateVerifyPage = () => {
  const { certificateId } = useParams();
  const [certificate, setCertificate] = useState(null);
  const [branding, setBranding] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const certResp = await axios.get(
          `${API_BASE}/api/lms/certificates/${encodeURIComponent(certificateId)}`,
        );
        if (cancelled) return;
        setCertificate(certResp.data);
        // Branding is optional — failure here must not block the page.
        try {
          const tenant = certResp.data.tenant_id;
          if (tenant) {
            const brandingResp = await axios.get(
              `${API_BASE}/api/tenant-branding/${encodeURIComponent(tenant)}/branding`,
            );
            if (!cancelled) setBranding(brandingResp.data);
          }
        } catch (_) {
          /* swallow — fall back to tenant_id-as-name in render */
        }
      } catch (e) {
        if (!cancelled) {
          if (e.response && e.response.status === 404) {
            setError("not_found");
          } else {
            setError("network");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [certificateId]);

  const tenantDisplay =
    (branding && branding.display_name) ||
    (certificate && certificate.tenant_id) ||
    "Client Organization";

  const pdfUrl =
    certificate && `${API_BASE}/api/lms/certificates/${encodeURIComponent(certificateId)}/download`;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center" data-testid="verify-loading">
        <div className="flex flex-col items-center gap-3 text-slate-300">
          <div className="h-10 w-10 border-2 border-[#00C8E0] border-t-transparent rounded-full animate-spin" />
          <p className="text-sm">Resolving certificate…</p>
        </div>
      </div>
    );
  }

  if (error === "not_found") {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center px-6" data-testid="verify-not-found">
        <div className="max-w-md w-full bg-[#0F1E35] border border-[#1E3A5F] rounded-lg p-8 text-center">
          <AlertTriangle className="mx-auto mb-4 text-amber-400" size={48} />
          <h1 className="text-xl font-semibold text-white mb-2">Certificate not found</h1>
          <p className="text-sm text-slate-400">
            We could not locate a Charstan-issued certificate with ID
            <span className="block font-mono text-slate-300 mt-1 break-all">{certificateId}</span>
          </p>
          <p className="text-xs text-slate-500 mt-4">
            If you scanned a QR code, please confirm with the issuing organization.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 mt-6 text-[#00C8E0] hover:underline text-sm"
            data-testid="verify-not-found-home-link"
          >
            <ArrowLeft size={14} /> Back to Charstan
          </Link>
        </div>
      </div>
    );
  }

  if (error || !certificate) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center px-6" data-testid="verify-error">
        <div className="max-w-md w-full bg-[#0F1E35] border border-[#1E3A5F] rounded-lg p-8 text-center">
          <AlertTriangle className="mx-auto mb-4 text-red-400" size={48} />
          <h1 className="text-xl font-semibold text-white mb-2">Unable to verify certificate</h1>
          <p className="text-sm text-slate-400">
            A network or service error prevented verification. Please try again in a moment.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 mt-6 text-[#00C8E0] hover:underline text-sm"
            data-testid="verify-error-home-link"
          >
            <ArrowLeft size={14} /> Back to Charstan
          </Link>
        </div>
      </div>
    );
  }

  const isRevoked = Boolean(certificate.revoked);

  return (
    <div className="min-h-screen bg-[#0A1628] text-slate-200" data-testid="verify-page">
      {/* Co-branding banner */}
      <header className="border-b border-[#1E3A5F] bg-[#0F1E35]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3" data-testid="verify-co-branding">
            <Globe2 className="text-[#00C8E0]" size={20} />
            <span className="text-xs uppercase tracking-wider text-slate-400">Powered by</span>
            <span className="text-white font-semibold">CHARSTAN</span>
            <span className="text-slate-500">·</span>
            <span className="text-white" data-testid="verify-tenant-display-name">{tenantDisplay}</span>
          </div>
          <Link
            to="/"
            className="text-xs text-[#00C8E0] hover:underline"
            data-testid="verify-charstan-home-link"
          >
            app.charstan.com →
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10">
        {isRevoked && (
          <div
            className="mb-6 rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 flex items-start gap-3"
            data-testid="verify-revoked-banner"
          >
            <AlertTriangle className="text-red-400 shrink-0 mt-0.5" size={18} />
            <div>
              <p className="text-sm font-semibold text-red-300">This certificate has been revoked.</p>
              <p className="text-xs text-red-300/80 mt-1">
                The issuing organization revoked this certificate. It is no longer valid as proof of training
                completion.
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Metadata column */}
          <section className="lg:col-span-2 space-y-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00C8E0]/10 border border-[#00C8E0]/30 text-[#00C8E0] text-xs uppercase tracking-wider mb-4">
                <ShieldCheck size={14} /> Certificate verified
              </div>
              <h1 className="text-3xl font-semibold text-white mb-2">Proficiency Certificate</h1>
              <p className="text-sm text-slate-400">
                This certificate was issued by{" "}
                <span className="text-slate-200 font-medium">{tenantDisplay}</span> through the CHARSTAN
                Learning Management System.
              </p>
            </div>

            <dl className="space-y-4">
              <div className="flex items-start gap-3" data-testid="verify-attendee-row">
                <UserIcon size={16} className="text-[#00C8E0] mt-0.5" />
                <div>
                  <dt className="text-xs uppercase tracking-wider text-slate-500 mb-0.5">Attendee</dt>
                  <dd className="text-white font-medium" data-testid="verify-attendee-name">
                    {certificate.attendee_name}
                  </dd>
                </div>
              </div>

              <div className="flex items-start gap-3" data-testid="verify-topic-row">
                <GraduationCap size={16} className="text-[#00C8E0] mt-0.5" />
                <div>
                  <dt className="text-xs uppercase tracking-wider text-slate-500 mb-0.5">Topic</dt>
                  <dd className="text-white" data-testid="verify-topic-title">
                    {certificate.topic_title}
                  </dd>
                </div>
              </div>

              <div className="flex items-start gap-3" data-testid="verify-date-row">
                <Calendar size={16} className="text-[#00C8E0] mt-0.5" />
                <div>
                  <dt className="text-xs uppercase tracking-wider text-slate-500 mb-0.5">Completion date</dt>
                  <dd className="text-white" data-testid="verify-completion-date">
                    {certificate.completion_date_utc
                      ? new Date(certificate.completion_date_utc).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        })
                      : "—"}
                  </dd>
                </div>
              </div>

              {certificate.proficiency_score != null && (
                <div className="flex items-start gap-3" data-testid="verify-score-row">
                  <Award size={16} className="text-[#00C8E0] mt-0.5" />
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-slate-500 mb-0.5">Proficiency score</dt>
                    <dd className="text-white" data-testid="verify-proficiency-score">
                      {certificate.proficiency_score} / 100
                    </dd>
                  </div>
                </div>
              )}

              <div className="flex items-start gap-3" data-testid="verify-instructor-row">
                <UserIcon size={16} className="text-[#00C8E0] mt-0.5" />
                <div>
                  <dt className="text-xs uppercase tracking-wider text-slate-500 mb-0.5">Instructor</dt>
                  <dd className="text-white" data-testid="verify-instructor-name">
                    {certificate.instructor_name}
                  </dd>
                </div>
              </div>

              <div className="flex items-start gap-3" data-testid="verify-id-row">
                <ShieldCheck size={16} className="text-[#00C8E0] mt-0.5" />
                <div>
                  <dt className="text-xs uppercase tracking-wider text-slate-500 mb-0.5">Certificate ID</dt>
                  <dd
                    className="text-white font-mono text-sm break-all"
                    data-testid="verify-certificate-id"
                  >
                    {certificate.certificate_id}
                  </dd>
                </div>
              </div>
            </dl>

            <div className="pt-2">
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={
                  "inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition " +
                  (isRevoked
                    ? "bg-slate-700 text-slate-400 cursor-not-allowed pointer-events-none"
                    : "bg-[#00C8E0] text-[#0A1628] hover:bg-[#33d4e8]")
                }
                data-testid="verify-download-pdf-btn"
                aria-disabled={isRevoked}
              >
                <Download size={16} />
                Download PDF
              </a>
              <p className="text-xs text-slate-500 mt-3">
                Co-branded landscape A4 PDF — opens in a new tab.
              </p>
            </div>
          </section>

          {/* PDF preview */}
          <section className="lg:col-span-3" data-testid="verify-pdf-preview-wrap">
            <div className="rounded-lg border border-[#1E3A5F] bg-[#0F1E35] overflow-hidden">
              {isRevoked ? (
                <div className="h-[640px] flex items-center justify-center text-slate-500 text-sm">
                  PDF preview hidden for revoked certificates.
                </div>
              ) : (
                <iframe
                  title={`Certificate ${certificate.certificate_id} preview`}
                  src={`${pdfUrl}#toolbar=0`}
                  className="w-full h-[640px]"
                  data-testid="verify-pdf-iframe"
                />
              )}
            </div>
          </section>
        </div>

        {/* Footer trust strip */}
        <footer className="mt-12 border-t border-[#1E3A5F] pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            Verified by CHARSTAN — Quality Management System &middot; CFR Part 11 §11.10(e) audit-trail integrity
          </p>
          <Link
            to="/"
            className="text-xs text-[#00C8E0] hover:underline"
            data-testid="verify-footer-charstan-link"
          >
            Learn about Charstan →
          </Link>
        </footer>
      </main>
    </div>
  );
};

export default CertificateVerifyPage;
